import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";

import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMLevelCategory {
  /** (number | string) The number or text to display inside the colored cell. */
  value: number | string;
  /** (string) The label shown below the colored cell. */
  label?: string;
  /** (string) Background color for this cell (any CSS color or theme key). */
  valueColor: string;
  /** (string) Text color inside the cell. */
  textColor: string;
}

export interface PMLevelCardProps extends Omit<PMBaseCardProps, "children"> {
  /** (PMLevelCategory[]) Array of category objects to display (only first five are rendered). */
  categories: PMLevelCategory[];
  labelPlacement?: "top" | "bottom";
}

/**
 * PMLevelCard renders a horizontal list of up to five colored category cells,
 * each showing a value and a label, all wrapped inside a PMBaseCard with header icons.
 *
 * Props:
 * - categories: an array of PMLevelCategory objects (value, label, valueColor, textColor).
 * - icon: optional header icon (string name resolves to a Material-UI icon).
 * - title: the title displayed in the card header.
 * - showInfoIcon: whether to show the info icon in the header (default: true).
 * - showMenuIcon: whether to show the menu icon in the header (default: true).
 * - ...rest: any additional PMBaseCardProps (e.g. sx, fixedWidth, fixedHeight).
 */
export const PMLevelCard: PimoReactComponent<PMLevelCardProps> = ({
  categories,
  icon = "InfoOutlined",
  title = "Title",
  labelPlacement = "bottom",
  showInfoIcon = true,
  showMenuIcon = true,
  ...rest
}) => {
  const theme = useTheme();

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      {...rest}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end",
          flexWrap: "wrap",
          px: 2,
          py: 1,
          gap: 2,
        }}
      >
        {categories.slice(0, 5).map((cat, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {cat.label && labelPlacement === "top" ? (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.primary }}
              >
                {cat.label}
              </Typography>
            ) : null}
            <Tooltip
              title={String(cat.value ?? "")}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#fff",
                    color: "#414141",
                    border: "1px solid",
                    borderColor: "background.paper",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    fontSize: "14px",
                    fontWeight: 400,
                    padding: "12px 16px",
                    borderRadius: "8px",
                    "& .MuiTooltip-arrow": {
                      color: "#fff",
                    },
                  },
                },
              }}
            >
              <Box
                sx={{
                  minHeight: 32,
                  minWidth: 68,
                  maxWidth: "100%",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 4,
                  bgcolor: cat.valueColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: cat.textColor,
                    m: 0,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    wordBreak: "break-word",
                    lineHeight: 1.2,
                  }}
                >
                  {cat.value}
                </Typography>
              </Box>
            </Tooltip>
            {cat.label && labelPlacement === "bottom" ? (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.primary }}
              >
                {cat.label}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </PMBaseCard>
  );
};

export default PMLevelCard;
